import type { Directive } from 'vue'
import { resolvePhotoUrl } from '@/utils/urlHelpers'

/**
 * v-img-fade="photoUrl" — пока фото грузится, показывает на его месте
 * скелетон-заглушку, а затем подставляет фото целиком.
 * Реальное фото скачивается отдельным Image() и декодируется (`decode()`),
 * и только готовое подставляется в `el.src`, поэтому рисуется мгновенно.
 * url передаётся значением директивы — она сама управляет `el.src`.
 */

interface FadeImage extends HTMLImageElement {
    _imgFadeToken?: number
}

// Прозрачный 1x1 GIF — заглушка, чтобы не было ни «стриминга», ни иконки
// битой картинки, пока грузится настоящее фото (фон-скелетон виден сквозь него).
const TRANSPARENT_PX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const LOADING_CLASS = 'img-fade--loading'

const apply = (el: FadeImage, rawSrc: string): void => {
    // Относительные ссылки на фото абсолютизируем по хосту клиента;
    // абсолютные и data:-URI проходят как есть.
    const src = resolvePhotoUrl(rawSrc)
    const token = (el._imgFadeToken ?? 0) + 1
    el._imgFadeToken = token

    const finish = (ok: boolean): void => {
        // Применяем результат, только если за это время не стартовала новая
        // загрузка и элемент не размонтирован.
        if (el._imgFadeToken !== token) {
            return
        }
        el.classList.remove(LOADING_CLASS)
        el.src = ok ? src : TRANSPARENT_PX
    }

    if (!src) {
        finish(false)
        return
    }

    const pre = new Image()
    pre.decoding = 'async'
    pre.src = src

    // Уже в кэше — показываем сразу, без мигания скелетона.
    if (pre.complete && pre.naturalWidth > 0) {
        el.classList.remove(LOADING_CLASS)
        el.src = src
        return
    }

    // Прячем настоящее фото за прозрачным пикселем и показываем скелетон.
    el.src = TRANSPARENT_PX
    el.classList.add(LOADING_CLASS)

    pre.onload = (): void => {
        // Дожидаемся полного декодирования, чтобы фото появилось целиком.
        if (typeof pre.decode === 'function') {
            pre.decode().then(() => finish(true)).catch(() => finish(true))
        } else {
            finish(true)
        }
    }
    pre.onerror = (): void => {
        finish(false)
    }
}

export const imgFade: Directive<FadeImage, string | null | undefined> = {
    mounted(el, binding) {
        apply(el, binding.value || '')
    },
    updated(el, binding) {
        // Реагируем только на реальную смену url — лишние перерисовки компонента игнорируем.
        if (binding.value === binding.oldValue) {
            return
        }
        apply(el, binding.value || '')
    },
    unmounted(el) {
        // Инвалидируем активную загрузку, чтобы её колбэк ничего не трогал.
        el._imgFadeToken = (el._imgFadeToken ?? 0) + 1
    }
}
